import { useEffect, useState, useCallback } from 'react';
import { api } from '../../api/client';
import { useUIStore } from '../../stores/uiStore';
import { RefreshCw, AlertCircle, PackageCheck } from 'lucide-react';
import { Can } from '../../lib/rbac';

type Verdict = 'no_data' | 'no_refund' | 'full_refund' | 'partial_refund';

interface ArbiterInfo {
  verdict: Verdict;
  amazon_charge: number;
  amazon_refund: number;
}

interface CancellationRow {
  ml_order_id: number;
  title: string | null;
  asin: string | null;
  paid_amount: number | string | null;
  amazon_cost: number | string | null;
  date_closed: string | null;
  context_site: string | null;
  arbiter: ArbiterInfo;
}

interface ResolveSummary {
  total: number;
  auto_resolved: number;
  left_pending: number;
}

// Texto humano y color por veredicto del árbitro — sin jerga.
const VERDICT_LABEL: Record<Verdict, { txt: string; cls: string }> = {
  full_refund: {
    txt: 'Amazon reembolsó todo',
    cls: 'bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/30',
  },
  partial_refund: {
    txt: 'Amazon reembolsó una parte',
    cls: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30',
  },
  no_refund: {
    txt: 'Amazon no reembolsó',
    cls: 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/30',
  },
  no_data: {
    txt: 'Amazon todavía no informó',
    cls: 'bg-gray-500/10 text-gray-600 dark:text-gray-400 border-gray-500/30',
  },
};

// Cada botón de decisión humana → un outcome válido del backend.
const DECISIONS: { outcome: string; label: string }[] = [
  { outcome: 'returned_to_seller_internal', label: 'Volvió al depósito' },
  { outcome: 'returned_to_amazon', label: 'Se devolvió a Amazon' },
  { outcome: 'lost_no_recovery', label: 'Se perdió' },
  { outcome: 'destroyed_by_ml', label: 'Lo destruyó Mercado Libre' },
];

const money = (v: number | string | null | undefined) =>
  v == null ? '—' : `$${Number(v).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`;

export function CancellationsSection() {
  const sellerId = useUIStore((s) => s.sellerId);
  const [rows, setRows] = useState<CancellationRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyOrder, setBusyOrder] = useState<number | null>(null);
  const [resolveMsg, setResolveMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ rows: CancellationRow[]; total: number }>(
        `/finance/cancellation-outcomes/pending?seller_id=${sellerId}&limit=200`
      );
      if (!res.success || !res.data) throw new Error(res.error || 'Error al cargar');
      setRows(res.data.rows ?? []);
      setTotal(res.data.total ?? 0);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [sellerId]);

  useEffect(() => {
    load();
  }, [load]);

  // Aplica la decisión humana sobre el destino del producto.
  const decide = async (mlOrderId: number, outcome: string) => {
    setBusyOrder(mlOrderId);
    setError(null);
    try {
      const res = await api.patch<unknown>(
        `/finance/cancellation-outcomes/${mlOrderId}?seller_id=${sellerId}`,
        { outcome }
      );
      if (!res.success) throw new Error(res.error || 'No se pudo guardar');
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyOrder(null);
    }
  };

  // Corre el resolvedor automático: revisa el reporte de Amazon.
  const runResolver = async () => {
    setLoading(true);
    setError(null);
    setResolveMsg(null);
    try {
      const res = await api.post<ResolveSummary>(
        `/finance/cancellation-outcomes/resolve?seller_id=${sellerId}`,
        {}
      );
      if (!res.success || !res.data) throw new Error(res.error || 'Falló la revisión');
      setResolveMsg(
        `Revisadas ${res.data.total}: ${res.data.auto_resolved} resueltas con Amazon, ` +
          `${res.data.left_pending} esperan tu decisión.`
      );
      await load();
    } catch (e) {
      setError((e as Error).message);
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-[var(--color-text)]">
            Cancelaciones a resolver
          </h2>
          <p className="text-sm text-[var(--color-text-secondary)]">
            {total} {total === 1 ? 'cancelación' : 'cancelaciones'} esperando una decisión.
          </p>
        </div>
        <Can cap="finance.operate">
          <button
            onClick={runResolver}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--color-primary)] text-white text-sm font-medium disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Revisar con Amazon
          </button>
        </Can>
      </div>

      {resolveMsg && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-green-500/10 text-green-700 dark:text-green-400 text-sm">
          <PackageCheck className="w-4 h-4 shrink-0" />
          {resolveMsg}
        </div>
      )}
      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 text-red-700 dark:text-red-400 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-3 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-12 text-[var(--color-text-secondary)]">
          <PackageCheck className="w-10 h-10 mx-auto mb-2 opacity-40" />
          No hay cancelaciones pendientes.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => {
            const v = VERDICT_LABEL[r.arbiter?.verdict] ?? VERDICT_LABEL.no_data;
            const busy = busyOrder === r.ml_order_id;
            return (
              <div
                key={r.ml_order_id}
                className="border border-[var(--color-border)] rounded-lg p-3 bg-[var(--color-bg-secondary)]"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-[var(--color-text)] truncate">
                      {r.title || `Orden ${r.ml_order_id}`}
                    </div>
                    <div className="text-xs text-[var(--color-text-secondary)] mt-0.5">
                      Orden {r.ml_order_id} · Venta {money(r.paid_amount)} · Costo Amazon{' '}
                      {money(r.amazon_cost)}
                    </div>
                  </div>
                  <span
                    className={`text-xs px-2 py-1 rounded border whitespace-nowrap ${v.cls}`}
                  >
                    {v.txt}
                  </span>
                </div>

                <Can cap="finance.operate">
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <span className="text-xs text-[var(--color-text-secondary)]">
                      ¿Qué pasó con el producto?
                    </span>
                    {DECISIONS.map((d) => (
                      <button
                        key={d.outcome}
                        disabled={busy}
                        onClick={() => decide(r.ml_order_id, d.outcome)}
                        className="px-2 py-1 text-xs rounded border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-bg)] disabled:opacity-50"
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>
                </Can>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
